/**
 * Quando è un evento, detto come lo direbbe una persona.
 *
 * ## Perché restituisce un caso e non una frase
 *
 * Perché la frase dipende dalla lingua, e il dominio non la conosce: «Stasera alle 20:45» in polacco
 * non è «parola per parola» la stessa costruzione, e il nome del giorno lo sa `Intl`, non noi. Qui si
 * decide **quale delle cinque cose dire**; a dirla ci pensa chi disegna, con la tabella delle sette
 * lingue e `Intl.DateTimeFormat`.
 *
 * È la stessa divisione per cui `liveness.ts` risponde `live` e non «IN DIRETTA».
 *
 * ## Perché «oggi» non si calcola sottraendo
 *
 * `start - now < 24 ore` **non è «oggi»**: alle 23 di lunedì, le 10 di martedì cadono dentro le
 * ventiquattro ore e non sono oggi. E `< 48 ore` non è domani. Si confrontano i **giorni di
 * calendario**, nel fuso dell'apparecchio — che è quello in cui vive chi guarda, non quello del
 * server.
 */

/**
 * Da che ora in poi è «stasera».
 *
 * Le 18 sono una scelta, non un fatto: è l'ora in cui in Italia una partita si annuncia «stasera» e
 * non «oggi pomeriggio». Sta qui con un nome perché un `>= 18` sparso nel codice è un numero che
 * nessuno sa più da dove viene.
 */
export const EVENING_FROM_HOUR = 18;

/**
 * Le cinque cose che si possono dire di un evento.
 *
 * `date` non porta la data: la formatta chi disegna, perché «giovedì 25 ottobre» è una stringa che
 * dipende dalla lingua. Qui c'è solo l'istante.
 */
export type EventWhen =
  /** Sta andando in onda adesso. Non si dice quando: si dice che è adesso. */
  | { readonly kind: 'live'; readonly startMs: number }
  /** Oggi, dalle 18 in avanti. */
  | { readonly kind: 'todayEvening'; readonly startMs: number }
  /** Oggi, prima delle 18. */
  | { readonly kind: 'today'; readonly startMs: number }
  | { readonly kind: 'tomorrow'; readonly startMs: number }
  /** Più in là: qui serve la data, perché «fra quattro giorni» nessuno lo tiene a mente. */
  | { readonly kind: 'date'; readonly startMs: number };

/**
 * Quando è questo evento, visto da `nowMs`.
 *
 * `live` vince su tutto: a un evento in onda non interessa che giorno è. Lo stato lo decide
 * `liveness.ts` sulla finestra della guida quando c'è, e arriva qui già deciso — non si ricalcola,
 * perché due posti che decidono «è in diretta» con due regole diverse divergono al primo caso
 * limite.
 *
 * Un evento **passato** e non in onda non ha un caso suo: risponde `date`, e chi disegna non dovrebbe
 * averlo fra le mani — l'hero toglie gli eventi finiti prima di arrivare qui.
 */
export function eventWhen(startMs: number, nowMs: number, live: boolean): EventWhen {
  if (live) return { kind: 'live', startMs };

  const start = new Date(startMs);
  const now = new Date(nowMs);

  if (sameDay(start, now)) {
    return start.getHours() >= EVENING_FROM_HOUR
      ? { kind: 'todayEvening', startMs }
      : { kind: 'today', startMs };
  }

  const tomorrow = new Date(nowMs);
  /*
    `setDate(getDate() + 1)` e non `nowMs + 86_400_000`.

    I giorni non sono tutti di ventiquattro ore: la notte in cui l'ora cambia ne ha ventitré o
    venticinque, e sommando i millisecondi «domani» diventa oggi o dopodomani per un'ora. `Date` sa
    quanto è lungo il giorno; l'aritmetica sui millisecondi no.
  */
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sameDay(start, tomorrow)) return { kind: 'tomorrow', startMs };

  return { kind: 'date', startMs };
}

/** Lo stesso giorno di calendario, nel fuso dell'apparecchio. */
function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}
